<template>
  <div style="display: flex; flex-direction: column">
    <div class="inline-flex q-mb-xs">
      <div class="move-row">
        <span
          v-html="moveTypeName(el.MovementType)"
        />
        <app-icon
          v-if="!hideInfoBtn && el.Id > 0"
          class="move-row__info q-mr-xs"
          name="info-circle-outline"
          width="18px"
          color="#979797"
          hover-color="#499259"
          cursor="pointer"
          :title="$t('info')"
          @click="openInfoDialog"
        />
        <div :class="['move-row']" v-html="displayHtml"></div>
      </div>
      <app-icon
        v-if="!hideRemoveBtn"
        class="ml-1 move-row__remove"
        name="close"
        width="16px"
        color="#979797"
        hover-color="#616161"
        cursor="pointer"
        :title="$t('remove')"
        @click.stop="detach"
      />
    </div>
    <div v-if="showPositions">
      <div
        v-for="(pos, idx) in el.Items"
        class="row no-wrap items-baseline"
        :key="'item_pos_' + idx"
      >
        {{ idx + 1 }})&nbsp;
        <!-- <parameter-inline-display
          :id="'ds_mr_' + pos.Id"
          :editable="false"
          :is-table="false"
          :model-value="getPositionFromNameInstance(pos)"
        /> -->
        <position-row
          :id="pos.Id"
          :el="getPositionFromNameInstance(pos)"
          :hide-info-btn="true"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useQuasar } from "quasar";
import { Component, Prop, Vue } from "vue-facing-decorator";
import { V2ParameterValueType } from "../../../../models/etc/parameter-types";
import {
  ParameterInstanceContainerType,
  V2FieldValueDisplayType,
  V2RecordAxisType,
  V2StorageItemMovementType,
} from "../../../../models/etc/types";
import { mapStorageItemInstanceToParameterInstance } from "../../../../models/helpers/parameter-helpers";
import {
  StorageItemInMovement,
  StorageItemMovementInFinances,
} from "../../../../models/interfaces/finances/finance-models";
import {
  ParameterInstance,
  ParameterInstanceContainer,
} from "../../../../models/interfaces/parameters/parameter-models";
import MoveInfoDialog from "./MoveInfoDialog.vue";
import PositionRow from "./PositionRow.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

// const $q = useQuasar()

@Component({
  name: "MovementRow",
  components: { PositionRow, MoveInfoDialog, AppIcon },
  emits: ["show-info", "detach"],
})
export default class MovementRow extends Vue {
  // === PROPS ===
  @Prop({ required: true }) readonly el!: StorageItemMovementInFinances;
  @Prop({ type: Boolean, default: false }) readonly hideInfoBtn!: boolean;
  @Prop({ type: Boolean, default: true }) readonly hideRemoveBtn!: boolean;
  @Prop({ type: Boolean, default: true }) readonly showPositions!: boolean;

  private $q = useQuasar();

  // showMoveInfoMode = false;

  // === Computed: вся строка в v-html ===
  get displayHtml(): string {
    // if (this.el.isLoading) return '<span class="text-italic">Загрузка...</span>';

    const parts: string[] = [];

    // Артикул
    parts.push(
      `<span class="q-mr-xs">${
        this.el.VendorCode ? this.el.VendorCode : this.$t('being_created')
      }</span>`
    );

    // Дата
    parts.push(
      `<span class="q-mr-xs">от ${this.formatDate(this.el.Date)}</span>`
    );

    // Склад откуда
    if (
      this.el.MovementType === V2StorageItemMovementType.Out ||
      this.el.MovementType === V2StorageItemMovementType.Inner
    ) {
      parts.push(`<span class="q-mr-xs">${this.el.SourceStorage?.Name}</span>`);
    }
    // ->
    if (this.el.MovementType === V2StorageItemMovementType.Inner) {
      parts.push(`<span class="q-mr-xs">-></span>`);
    }
    // Склад откуда
    if (
      this.el.MovementType === V2StorageItemMovementType.In ||
      this.el.MovementType === V2StorageItemMovementType.Inner
    ) {
      parts.push(
        `<span class="q-mr-xs">${this.el.ReceiverStorage?.Name}</span>`
      );
    }

    // Комментарий
    if (this.el.Note) {
      parts.push(
        `<span class="q-mr-xs">${this.highlightText(this.el.Note)}</span>`
      );
    }

    return parts.join("");
  }

  openInfoDialog() {
    this.$q.dialog({
      component: MoveInfoDialog,
      componentProps: {
        existingMovementId: this.el.Id,
        createType: this.el.MovementType
      }
    })
    .onOk(() => {
      console.log('Диалог успешно закрыт (пользователь сохранил или просто закрыл)')
      // Здесь можно обновить список, перезагрузить данные и т.д.
    })
    .onCancel(() => {
      console.log('Отмена (если добавишь onDialogCancel)')
    })
    .onDismiss(() => {
      console.log('Диалог закрыт любым способом')
    })
  }

  getPositionFromNameInstance(pos: StorageItemInMovement) {
    const pos_copy = mapStorageItemInstanceToParameterInstance(
      pos.NameParameters
    );
    const vcode_cell = {
      Type: ParameterInstanceContainerType.SingleInstance,
      Instance: {
        TemplateId: 999,
        Unremovable: true,
        ValueType: V2ParameterValueType.String,
        StringValue: pos.VendorCode,
        NumberValue: null,
        DateValue: null,
        BoolValue: null,
        ValueSet: null,
        SelectedValue: null,
        ValueDisplayType: V2FieldValueDisplayType.Inline,
        Name: this.$t('sku'),
        Id: 0,
        Round: 2,
        RecordAxisType: V2RecordAxisType.Horizontal,
        RecordFields: [],
      } as ParameterInstance,
      ListTitle: null,
    } as ParameterInstanceContainer;

    return [vcode_cell, ...pos_copy];
  }

  // movement type icon span
  moveTypeName(arg: number): string {
    switch (arg) {
      // case MovementTypeFilterFlagsDto.Both:
      // return this.$t('both_label')
      case V2StorageItemMovementType.Out:
        return '<span class="no-break movement-element movement-element--expense"/>';
      case V2StorageItemMovementType.In:
        return '<span class="no-break movement-element movement-element--income"/>';
      case V2StorageItemMovementType.Inner:
        return `<span class="no-break movement-element movement-element--transfer"/>`;
      default:
        return this.$t("filter_empty");
    }
  }

  // === Методы ===
  highlightText(text: string): string {
    // Здесь должна быть логика фильтрации (например, из глобального поиска)
    // Пока — просто экранируем
    return text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  formatDate(date: Date | string): string {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}.${d.getFullYear().toString().slice(-2)}`;
  }

  // === События ===
  showInfo() {
    this.$emit("show-info", { Id: this.el.Id, Type: this.el.MovementType });
  }

  detach() {
    this.$emit("detach", { Id: this.el.Id, Type: this.el.MovementType });
  }
}
</script>

<style lang="scss" scoped>
.move-row {
  display: inline;
}

.move-row :deep(.green-text) {
  color: $primary;
  font-weight: 500;
}

.move-row :deep(.text-medium) {
  font-weight: 500;
}

.move-row :deep(.text-span) {
  color: $text-color;
  font-style: italic;
}

.inline-flex {
  display: inline-flex;
  align-items: flex-start;
}

.move-row__info {
  margin-top: -2px;
}

.move-row__remove {
  margin-top: 1px;
}
</style>
